import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Heart, MessageCircle, User, X, Send, ThumbsUp } from 'lucide-react'
import { useProjectStore } from '../stores/projectStore'
import { Scene, Image, Comment } from '../types'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { createGuestUser, saveGuestComment, toggleGuestLike } from '../lib/firebase/guests'

const ProjectViewer: React.FC = () => {
  const { id: projectId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { fetchScenes, fetchImages, loading } = useProjectStore()
  
  const [project, setProject] = useState<any>(null)
  const [scenes, setScenes] = useState<Scene[]>([])
  const [images, setImages] = useState<Image[]>([])
  const [selectedSceneId, setSelectedSceneId] = useState<string | null>(null)
  const [selectedImage, setSelectedImage] = useState<Image | null>(null)
  const [showUsernamePrompt, setShowUsernamePrompt] = useState(false)
  const [guestUsername, setGuestUsername] = useState('')
  const [tempUsername, setTempUsername] = useState('')
  const [commentText, setCommentText] = useState('')
  const [showComments, setShowComments] = useState(false)

  useEffect(() => {
    // Check if guest username is stored in localStorage
    const storedUsername = localStorage.getItem('guestUsername')
    if (storedUsername) {
      setGuestUsername(storedUsername)
    } else {
      setShowUsernamePrompt(true)
    }
  }, [])

  useEffect(() => {
    if (!projectId) return

    const loadProject = async () => {
      try {
        // Fetch project directly from Firestore
        const projectDoc = await getDoc(doc(db, 'projects', projectId))
        
        if (projectDoc.exists()) {
          const docData = projectDoc.data()
          const projectData: any = {
            id: projectDoc.id,
            ...docData,
            createdAt: docData.createdAt?.toDate().toISOString(),
            updatedAt: docData.updatedAt?.toDate().toISOString(),
          }
          
          // Check if project is public
          if (!projectData.isPublic) {
            alert('This project is not public')
            navigate('/')
            return
          }
          
          setProject(projectData)
          
          // Load scenes and images
          await loadScenes(projectId)
        } else {
          alert('Project not found')
          navigate('/')
        }
      } catch (error) {
        console.error('Failed to load project:', error)
        alert('Failed to load project')
        navigate('/')
      }
    }

    loadProject()
  }, [projectId, navigate])

  const loadScenes = async (projectId: string) => {
    try {
      await fetchScenes(projectId)
      // Get scenes from store
      const storeScenes = useProjectStore.getState().scenes
      setScenes(storeScenes)
      
      if (storeScenes.length > 0) {
        setSelectedSceneId(storeScenes[0].id)
        await loadImages(storeScenes[0].id)
      }
    } catch (error) {
      console.error('Failed to load scenes:', error)
    }
  }

  const loadImages = async (sceneId: string) => {
    try {
      await fetchImages(sceneId)
      // Get images from store
      const storeImages = useProjectStore.getState().images
      setImages(storeImages)
    } catch (error) {
      console.error('Failed to load images:', error)
    }
  }

  const handleSetUsername = async () => {
    if (!tempUsername.trim()) return
    
    const username = tempUsername.trim()
    
    try {
      // Create guest user in Firebase
      const guestId = await createGuestUser(username)
      
      // Store both username and guest ID
      localStorage.setItem('guestUsername', username)
      localStorage.setItem('guestId', guestId)
      
      setGuestUsername(username)
      setShowUsernamePrompt(false)
    } catch (error) {
      console.error('Failed to create guest user:', error)
      // Still allow local usage
      localStorage.setItem('guestUsername', username)
      setGuestUsername(username)
      setShowUsernamePrompt(false)
    }
  }

  const handleSceneSelect = async (sceneId: string) => {
    setSelectedSceneId(sceneId)
    await loadImages(sceneId)
  }

  const handleLikeImage = async (imageId: string) => {
    if (!guestUsername) {
      setShowUsernamePrompt(true)
      return
    }
    
    try {
      // Toggle like in Firebase
      const isLiked = await toggleGuestLike(imageId, guestUsername)
      
      // Update local state
      setImages(prevImages => 
        prevImages.map(img => {
          if (img.id === imageId) {
            const currentLikedBy = img.likedBy || []
            return {
              ...img,
              likes: isLiked ? (img.likes || 0) + 1 : Math.max(0, (img.likes || 0) - 1),
              likedBy: isLiked 
                ? [...currentLikedBy, guestUsername]
                : currentLikedBy.filter(name => name !== guestUsername)
            }
          }
          return img
        })
      )
      
      // Update selected image if it's the one being liked
      if (selectedImage && selectedImage.id === imageId) {
        const updatedImage = images.find(img => img.id === imageId)
        if (updatedImage) {
          setSelectedImage({
            ...updatedImage,
            likes: isLiked ? (updatedImage.likes || 0) + 1 : Math.max(0, (updatedImage.likes || 0) - 1),
            likedBy: isLiked 
              ? [...(updatedImage.likedBy || []), guestUsername]
              : (updatedImage.likedBy || []).filter(name => name !== guestUsername)
          })
        }
      }
    } catch (error) {
      console.error('Failed to like image:', error)
      alert('Failed to like image. Please try again.')
    }
  }

  const handleAddComment = async () => {
    if (!commentText.trim() || !selectedImage) return
    if (!guestUsername) {
      setShowUsernamePrompt(true)
      return
    }
    
    try {
      // Save comment to Firebase
      await saveGuestComment(selectedImage.id, guestUsername, commentText.trim())
      
      // Create comment object for local state
      const newComment: Comment = {
        id: Date.now().toString(),
        imageId: selectedImage.id,
        userId: 'guest_' + Date.now(),
        userEmail: '',
        username: guestUsername,
        isGuest: true,
        content: commentText.trim(),
        createdAt: new Date().toISOString(),
        replies: [],
        likes: 0
      }
      
      // Update local state
      setImages(prevImages =>
        prevImages.map(img =>
          img.id === selectedImage.id
            ? { ...img, comments: [...img.comments, newComment] }
            : img
        )
      )
      
      setSelectedImage({
        ...selectedImage,
        comments: [...selectedImage.comments, newComment]
      })
      
      setCommentText('')
    } catch (error) {
      console.error('Failed to add comment:', error)
      alert('Failed to add comment. Please try again.')
    }
  }

  const sceneImages = images.filter(img => img.sceneId === selectedSceneId)

  if (loading && !project) {
    return (
      <div className="min-h-screen bg-primary-700 flex items-center justify-center">
        <div className="glass-card p-8 text-center">
          <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white font-body font-light">Loading project...</p>
        </div>
      </div>
    )
  }

  if (!project) {
    return null
  }

  return (
    <div className="min-h-screen bg-primary-700">
      {/* Username Prompt Dialog */}
      <AnimatePresence>
        {showUsernamePrompt && !guestUsername && (
          <motion.div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="glass-card w-full max-w-md p-6 border border-white/20"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-primary-500/20 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-primary-300" />
                </div>
                <h2 className="text-xl font-semibold text-white">Enter Your Name</h2>
              </div>
              
              <p className="text-white/70 text-sm mb-4">
                To interact with this project (like images and leave comments), please enter a display name.
              </p>
              
              <input
                type="text"
                value={tempUsername}
                onChange={(e) => setTempUsername(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSetUsername()}
                placeholder="Your name..."
                className="glass-input w-full mb-4"
                maxLength={50}
                autoFocus
              />
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowUsernamePrompt(false)}
                  className="glass-button text-white/90 hover:bg-white/10 px-4 py-2"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSetUsername}
                  className="glass-button bg-primary-500/30 hover:bg-primary-500/40 text-white px-4 py-2"
                  disabled={!tempUsername.trim()}
                >
                  Continue
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="border-b border-white/10 bg-primary-800/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="glass-button p-2 hover:bg-white/20"
              >
                <ArrowLeft className="w-5 h-5 text-white" />
              </button>
              <div>
                <h1 className="text-2xl font-semibold text-white">{project.title}</h1>
                {project.description && (
                  <p className="text-white/70 text-sm mt-1">{project.description}</p>
                )}
              </div>
            </div>
            
            {guestUsername && (
              <div className="flex items-center space-x-2 glass-card px-3 py-2">
                <User className="w-4 h-4 text-primary-300" />
                <span className="text-white text-sm">{guestUsername}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-6">
        {/* Scenes Navigation */}
        {scenes.length > 1 && (
          <div className="mb-6">
            <div className="flex space-x-2 overflow-x-auto pb-2">
              {scenes.map((scene) => (
                <button
                  key={scene.id}
                  onClick={() => handleSceneSelect(scene.id)}
                  className={`glass-button px-4 py-2 whitespace-nowrap ${
                    selectedSceneId === scene.id
                      ? 'bg-primary-500/30 text-white'
                      : 'text-white/70 hover:text-white'
                  }`}
                >
                  {scene.title}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Image Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sceneImages.map((image) => (
            <motion.div
              key={image.id}
              className="glass-card overflow-hidden cursor-pointer group"
              onClick={() => {
                setSelectedImage(image)
                setShowComments(true)
              }}
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
            >
              <div className="relative aspect-video">
                <img
                  src={image.url}
                  alt={image.name}
                  className="w-full h-full object-cover"
                />
                
                {/* Overlay with actions */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center space-x-4">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleLikeImage(image.id)
                    }}
                    className="glass-button p-3 hover:bg-white/20"
                  >
                    <Heart 
                      className={`w-6 h-6 ${
                        image.likedBy?.includes(guestUsername) 
                          ? 'fill-red-500 text-red-500' 
                          : 'text-white'
                      }`}
                    />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setSelectedImage(image)
                      setShowComments(true)
                    }}
                    className="glass-button p-3 hover:bg-white/20"
                  >
                    <MessageCircle className="w-6 h-6 text-white" />
                  </button>
                </div>
              </div>
              
              {/* Image Info */}
              <div className="p-4">
                <h3 className="text-white font-medium mb-2">{image.name}</h3>
                <div className="flex items-center space-x-4 text-sm text-white/60">
                  <div className="flex items-center space-x-1">
                    <Heart className="w-4 h-4" />
                    <span>{image.likes || 0}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <MessageCircle className="w-4 h-4" />
                    <span>{image.comments.length}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {sceneImages.length === 0 && (
          <div className="text-center py-12">
            <p className="text-white/70">No images in this scene yet</p>
          </div>
        )}
      </div>

      {/* Image Detail Dialog with Comments */}
      <AnimatePresence>
        {selectedImage && showComments && (
          <motion.div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              setSelectedImage(null)
              setShowComments(false)
            }}
          >
            <motion.div
              className="glass-card w-full max-w-6xl max-h-[90vh] overflow-hidden border border-white/20 flex flex-col"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-white/10">
                <h3 className="text-white font-medium">{selectedImage.name}</h3>
                <button
                  onClick={() => {
                    setSelectedImage(null)
                    setShowComments(false)
                  }}
                  className="glass-button p-2 hover:bg-white/20"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto flex flex-col md:flex-row">
                {/* Image */}
                <div className="flex-1 p-4 flex items-center justify-center bg-black/20">
                  <img
                    src={selectedImage.url}
                    alt={selectedImage.name}
                    className="max-w-full max-h-[60vh] object-contain"
                  />
                </div>

                {/* Comments Sidebar */}
                {project.allowComments !== false && (
                  <div className="w-full md:w-96 border-t md:border-t-0 md:border-l border-white/10 flex flex-col">
                    <div className="p-4 border-b border-white/10">
                      <div className="flex items-center space-x-4 text-white/80">
                        <button
                          onClick={() => handleLikeImage(selectedImage.id)}
                          className="flex items-center space-x-2 hover:text-red-400 transition-colors"
                        >
                          <Heart 
                            className={`w-5 h-5 ${
                              selectedImage.likedBy?.includes(guestUsername)
                                ? 'fill-red-500 text-red-500'
                                : ''
                            }`}
                          />
                          <span>{selectedImage.likes || 0} likes</span>
                        </button>
                      </div>
                    </div>

                    {/* Comments List */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                      {selectedImage.comments.length === 0 ? (
                        <p className="text-white/60 text-center py-8">No comments yet</p>
                      ) : (
                        selectedImage.comments.map((comment) => (
                          <div key={comment.id} className="glass-card p-3">
                            <div className="flex items-start space-x-2">
                              <div className="w-8 h-8 bg-primary-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                                <User className="w-4 h-4 text-primary-300" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-2 mb-1">
                                  <span className="text-white text-sm font-medium">
                                    {comment.username || comment.userEmail}
                                  </span>
                                  {comment.isGuest && (
                                    <span className="text-xs text-white/50">Guest</span>
                                  )}
                                </div>
                                <p className="text-white/80 text-sm">{comment.content}</p>
                                <div className="flex items-center space-x-3 mt-2">
                                  <button className="text-xs text-white/50 hover:text-white flex items-center space-x-1">
                                    <ThumbsUp className="w-3 h-3" />
                                    <span>{comment.likes || 0}</span>
                                  </button>
                                  <span className="text-xs text-white/50">
                                    {new Date(comment.createdAt).toLocaleDateString()}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    {/* Add Comment */}
                    <div className="p-4 border-t border-white/10">
                      {guestUsername ? (
                        <div className="flex space-x-2">
                          <input
                            type="text"
                            value={commentText}
                            onChange={(e) => setCommentText(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
                            placeholder="Add a comment..."
                            className="glass-input flex-1"
                          />
                          <button
                            onClick={handleAddComment}
                            className="glass-button bg-primary-500/30 hover:bg-primary-500/40 text-white p-2"
                            disabled={!commentText.trim()}
                          >
                            <Send className="w-5 h-5" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setShowUsernamePrompt(true)}
                          className="glass-button w-full bg-primary-500/30 hover:bg-primary-500/40 text-white py-2"
                        >
                          Sign in as Guest to Comment
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default ProjectViewer

